
import { useState, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { DashboardCard } from "./DashboardCard";
import { Progress } from "@/components/ui/progress";
import { CheckSquare, AlertTriangle, ClipboardList, ShieldAlert, ShieldCheck, DollarSign } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { getOverallScore } from "@/services/scoring/scoringQueries";
import { EmptyState } from "@/components/ui/EmptyState";
import { useNavigate } from "react-router-dom";
import { Skeleton } from "@/components/ui/skeleton";
import { SuccessionRisksOverlay } from "./SuccessionRisksOverlay";
import { Badge } from "@/components/ui/badge";
import { useTeamId } from "@/hooks/useTeamId";
import { useCompanyData } from "@/hooks/useCompanyData";

interface DashboardMetricsProps {
  onRisksClick?: () => void;
  onScoreClick?: () => void;
}

export interface RiskItem {
  name: string;
  code: string;
  score: number;
  level: 'high' | 'medium';
}

export function DashboardMetrics({ onRisksClick, onScoreClick }: DashboardMetricsProps) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { effectiveTeamId, loading: teamLoading } = useTeamId();
  const { teamData } = useCompanyData();
  const [risksOverlayOpen, setRisksOverlayOpen] = useState(false);

  // Score tile — useQuery with invalidatable key
  const { data: scoreData, isLoading: scoreLoading } = useQuery({
    queryKey: ['userScores', user?.id, effectiveTeamId],
    queryFn: async () => {
      const result = await getOverallScore(user!.id, effectiveTeamId);
      const { count } = await supabase
        .from('assessment_modules')
        .select('*', { count: 'exact', head: true })
        .eq('user_id', user!.id)
        .eq('status', 'complete');
      return {
        score: result.score,
        isPreliminary: result.isPreliminary,
        completedAssessments: count ?? 0,
      };
    },
    staleTime: 30_000,
    enabled: !!user?.id && !teamLoading,
  });

  const mySuccessionScore = scoreData?.score ?? null;
  const isPreliminary = scoreData?.isPreliminary ?? true;
  const completedAssessments = scoreData?.completedAssessments ?? 0;
  const totalAssessments = 11;

  // Risks tile — useQuery with invalidatable key
  const { data: risksData, isLoading: risksLoading } = useQuery({
    queryKey: ['userMcfValues', user?.id, effectiveTeamId],
    queryFn: async () => {
      const { data: mcfValues, error } = await supabase
        .from('user_mcf_values')
        .select('calculated_value, measurement_component_factors(code, mcf_name)')
        .eq('user_id', user!.id)
        .eq('team_id', effectiveTeamId);

      if (error) {
        console.error('[Risks] Query error:', error.message);
        return { hasAnyMcfData: false, highRiskCount: 0, mediumRiskCount: 0, topRisks: [] as RiskItem[] };
      }

      if (!mcfValues || mcfValues.length === 0) {
        return { hasAnyMcfData: false, highRiskCount: 0, mediumRiskCount: 0, topRisks: [] as RiskItem[] };
      }

      const highRisks: RiskItem[] = [];
      const mediumRisks: RiskItem[] = [];

      for (const row of mcfValues) {
        const val = row.calculated_value as any;
        const score = val?.score != null ? Number(val.score) : null;
        if (score == null) continue;

        const mcf = row.measurement_component_factors as any;
        const name = mcf?.mcf_name || 'Unknown Factor';
        const code = mcf?.code || '';

        if (score < 40) {
          highRisks.push({ name, code, score, level: 'high' });
        } else if (score <= 60) {
          mediumRisks.push({ name, code, score, level: 'medium' });
        }
      }

      highRisks.sort((a, b) => a.score - b.score);
      mediumRisks.sort((a, b) => a.score - b.score);

      const top5: RiskItem[] = [...highRisks.slice(0, 5)];
      if (top5.length < 5) {
        top5.push(...mediumRisks.slice(0, 5 - top5.length));
      }

      return {
        hasAnyMcfData: true,
        highRiskCount: highRisks.length,
        mediumRiskCount: mediumRisks.length,
        topRisks: top5,
      };
    },
    staleTime: 30_000,
    enabled: !!user?.id && !teamLoading,
  });

  const highRiskCount = risksData?.highRiskCount ?? 0;
  const mediumRiskCount = risksData?.mediumRiskCount ?? 0;
  const topRisks = risksData?.topRisks ?? [];
  const hasAnyMcfData = risksData?.hasAnyMcfData ?? false;

  const displayScore = mySuccessionScore ?? 0;
  const hasScore = mySuccessionScore !== null;
  const noRisks = highRiskCount === 0 && mediumRiskCount === 0;

  return (
    <>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {scoreLoading ? (
          <DashboardCard
            title="mySuccession Score"
            className="animate-enter-dashboard"
          >
            <div className="space-y-3 mt-2">
              <Skeleton className="h-8 w-20" />
              <Skeleton className="h-2 w-full" />
              <Skeleton className="h-3 w-48" />
            </div>
          </DashboardCard>
        ) : !hasScore ? (
          <DashboardCard
            title="mySuccession Score"
            className="animate-enter-dashboard"
          >
            <EmptyState
              icon={ClipboardList}
              title="No Score Yet"
              description="Complete your first assessment to see your mySuccession Score"
              actionLabel="Start Assessment"
              onAction={() => navigate("/assessment")}
              className="py-4"
            />
          </DashboardCard>
        ) : (
          <DashboardCard
            title="mySuccession Score"
            value={`${displayScore}%`}
            icon={<CheckSquare className="h-4 w-4" />}
            trend={{ value: 0, isPositive: true }}
            className="animate-enter-dashboard cursor-pointer hover:shadow-lg transition-shadow"
            onClick={onScoreClick}
          >
            <Progress value={displayScore} className="h-2 mt-2" />
            <p className="text-xs text-muted-foreground mt-2">
              {isPreliminary
                ? "Preliminary — complete more assessments"
                : "Overall readiness for succession"}
            </p>
          </DashboardCard>
        )}

        {risksLoading ? (
          <DashboardCard
            title="Succession Risks"
            className="animate-enter-dashboard"
            animationDelay="100ms"
          >
            <div className="space-y-3 mt-2">
              <Skeleton className="h-6 w-32" />
              <Skeleton className="h-6 w-28" />
            </div>
          </DashboardCard>
        ) : !hasAnyMcfData ? (
          <DashboardCard
            title="Succession Risks"
            className="animate-enter-dashboard"
            animationDelay="100ms"
          >
            <EmptyState
              icon={AlertTriangle}
              title="No Risks Identified Yet"
              description="Complete your assessments to identify risks in your succession plan."
              actionLabel="Start Assessment"
              onAction={() => navigate("/assessment")}
              className="py-4"
            />
          </DashboardCard>
        ) : noRisks ? (
          <DashboardCard
            title="Succession Risks"
            icon={<ShieldCheck className="h-4 w-4 text-success" />}
            className="animate-enter-dashboard"
            animationDelay="100ms"
          >
            <div className="flex items-center gap-2 mt-2">
              <p className="text-sm text-success font-medium">
                No risks identified — your succession plan is on track
              </p>
            </div>
          </DashboardCard>
        ) : (
          <DashboardCard
            title="Succession Risks"
            icon={<ShieldAlert className="h-4 w-4 text-destructive" />}
            className="animate-enter-dashboard cursor-pointer hover:shadow-lg transition-shadow"
            animationDelay="100ms"
            onClick={() => setRisksOverlayOpen(true)}
          >
            <div className="space-y-2 mt-2">
              {highRiskCount > 0 && (
                <div className="flex items-center gap-2">
                  <Badge variant="destructive" className="text-xs">
                    {highRiskCount} high priority
                  </Badge>
                </div>
              )}
              {mediumRiskCount > 0 && (
                <div className="flex items-center gap-2">
                  <Badge variant="outline" className="text-xs border-warning text-warning">
                    {mediumRiskCount} medium priority
                  </Badge>
                </div>
              )}
              <p className="text-xs text-muted-foreground mt-1">Click to view details</p>
            </div>
          </DashboardCard>
        )}

        <DashboardCard
          title="Business Valuation"
          className="animate-enter-dashboard"
          animationDelay="200ms"
        >
          {teamData?.firm_valuation != null ? (
            <div className="space-y-2 py-2">
              <div className="text-3xl font-bold text-foreground">
                {new Intl.NumberFormat("en-CA", {
                  style: "currency",
                  currency: "CAD",
                  maximumFractionDigits: 0,
                }).format(teamData.firm_valuation)}
              </div>
              {teamData.firm_valuation_method && (
                <p className="text-xs text-muted-foreground">
                  Method: {teamData.firm_valuation_method}
                </p>
              )}
            </div>
          ) : (
            <EmptyState
              icon={ClipboardList}
              title="No Valuation Yet"
              description="Complete your Business Valuation assessment to see your estimated practice value."
              className="py-4"
            />
          )}
        </DashboardCard>
      </div>

      <SuccessionRisksOverlay
        open={risksOverlayOpen}
        onOpenChange={setRisksOverlayOpen}
        risks={topRisks}
        highCount={highRiskCount}
        mediumCount={mediumRiskCount}
      />
    </>
  );
}
